import DoneAll from "@mui/icons-material/DoneAll";
import Drafts from "@mui/icons-material/Drafts";
import InsertEmoticonRounded from "@mui/icons-material/InsertEmoticonRounded";
import Send from "@mui/icons-material/Send";
import TouchApp from "@mui/icons-material/TouchApp";
import Warning from "@mui/icons-material/Warning";
import WebAssetOutlined from "@mui/icons-material/WebAssetOutlined";
import { Grid, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import {
  isChatChannel,
  isEmailChannel,
  isWhatsAppChannel,
} from "../../../../modules/channels/types";
import { calculatePercentage } from "../utils";
import { WhatsAppAnalyticsViewToggle } from "../whatsapp-analytics-view-toggle";
import { AnalyticsOverviewClicks } from "./item/clicks";
import { AnalyticsOverviewItem } from "./item/item";
import { AnalyticsOverviewPercentage } from "./item/percentage";
import { AnalyticsOverviewReactions } from "./item/reactions";

import type { ChannelName } from "../../../../modules/channels/types";
import type { MomentAnalyticsTotals } from "../../../../modules/stats/server/analytics-service";
import type { FC } from "react";

export const AnalyticsOverviewContent: FC<{
  loading: boolean;
  data: MomentAnalyticsTotals | null;
  channel_name: ChannelName;
  target_channel: boolean;
}> = ({ loading, data, channel_name, target_channel }) => {
  const [whatsAppView, setWhatsAppView] = useState<"message" | "linked_page">("message");

  if (loading || !data) {
    return (
      <Skeleton
        data-testid="analytics-overview-skeleton"
        animation="wave"
        variant="rounded"
        height={100}
      />
    );
  }

  const isWhatsApp = isWhatsAppChannel(channel_name);

  return (
    <Stack spacing={2}>
      {isWhatsApp && (
        <WhatsAppAnalyticsViewToggle
          value={whatsAppView}
          onChange={setWhatsAppView}
          analyticsIdPrefix="whatsapp-overview"
        />
      )}
      <Paper elevation={6} sx={{ px: 3, py: 2 }}>
        <Grid container spacing={8}>
          {(!isWhatsApp || whatsAppView === "message") && (
            <AnalyticsOverviewItem
              title="Delivered"
              icon={<Send color="primary" fontSize="small" />}
              tooltip="Messages that were delivered successfully for every time this Communication has been sent."
            >
              <AnalyticsOverviewPercentage
                data={{ count: data.delivery_count, total: data.total_sends }}
              />
            </AnalyticsOverviewItem>
          )}
          {isEmailChannel(channel_name) && (
            <AnalyticsOverviewItem
              title="Unique opens"
              icon={<Drafts color="primary" fontSize="small" />}
              tooltip="Messages that were opened by unique individuals for every time this Communication has been sent."
            >
              <AnalyticsOverviewPercentage
                data={{ count: data.first_open_count ?? 0, total: data.delivery_count ?? 0 }}
              />
            </AnalyticsOverviewItem>
          )}
          {isWhatsApp && whatsAppView === "message" && (
            <AnalyticsOverviewItem
              title="Read"
              icon={<DoneAll color="primary" fontSize="small" />}
              tooltip="Messages that were read by recipients for every time this Communication has been sent."
            >
              <AnalyticsOverviewPercentage
                data={{ count: data.first_open_count ?? 0, total: data.delivery_count ?? 0 }}
              />
            </AnalyticsOverviewItem>
          )}
          {!target_channel && !isWhatsApp && (
            <AnalyticsOverviewItem
              title="Interacted"
              icon={<TouchApp color="primary" fontSize="small" />}
              tooltip="Messages where recipients clicked on a link/button for every time this Communication has been sent."
            >
              <AnalyticsOverviewPercentage
                data={{
                  count: data.click_count ?? 0,
                  total:
                    (isEmailChannel(channel_name) ? data.first_open_count : data.delivery_count) ??
                    0,
                }}
              />
            </AnalyticsOverviewItem>
          )}
          {target_channel && !isWhatsApp && (
            <AnalyticsOverviewItem
              title="Clicks"
              icon={<TouchApp color="primary" fontSize="small" />}
              tooltip="Total number of link and button clicks by all recipients in the channel."
            >
              <AnalyticsOverviewClicks data={{ count: data.total_clicks }} />
            </AnalyticsOverviewItem>
          )}
          {isWhatsApp && whatsAppView === "message" && (
            <AnalyticsOverviewItem
              title="Clickthroughs"
              icon={<TouchApp color="primary" fontSize="small" />}
              tooltip="Messages where recipients clicked through for every time this Communication has been sent."
            >
              <AnalyticsOverviewPercentage
                data={{
                  count: data.click_count ?? 0,
                  total: data.delivery_count ?? 0,
                }}
              />
            </AnalyticsOverviewItem>
          )}
          {isChatChannel(channel_name) && (
            <AnalyticsOverviewItem
              title="Reactions"
              icon={<InsertEmoticonRounded color="primary" fontSize="small" />}
              tooltip="Emoji reactions to the message, for every time this Communication has been sent."
            >
              <AnalyticsOverviewReactions reactions={data.reaction_summary} />
            </AnalyticsOverviewItem>
          )}
          {(!isWhatsApp || whatsAppView === "message") && (
            <AnalyticsOverviewItem
              title="Issues"
              icon={<Warning color="primary" fontSize="small" />}
              tooltip="Individual delivery issues for every time this Communication has been sent."
            >
              <AnalyticsOverviewPercentage
                data={{ count: data.error_count ?? 0, total: data.total_sends }}
              />
            </AnalyticsOverviewItem>
          )}
          {isWhatsApp && whatsAppView === "linked_page" && (
            <>
              <AnalyticsOverviewItem
                title="Visitors"
                icon={<TouchApp color="primary" fontSize="small" />}
                mdSize={4}
              >
                <Typography variant="h5">{data.click_count ?? 0}</Typography>
              </AnalyticsOverviewItem>
              <AnalyticsOverviewItem
                title="Interacted"
                icon={<TouchApp color="primary" fontSize="small" />}
                mdSize={4}
              >
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography variant="h5">
                    {`${calculatePercentage(data.click_count ?? 0, data.delivery_count ?? 0)}%`}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    ({data.click_count ?? 0} {data.click_count === 1 ? "visitor" : "visitors"})
                  </Typography>
                </Stack>
              </AnalyticsOverviewItem>
              <AnalyticsOverviewItem
                title="Page views"
                icon={<WebAssetOutlined color="primary" fontSize="small" />}
                mdSize={4}
              >
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography variant="h5">{data.first_open_count ?? 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    from {data.click_count ?? 0} {data.click_count === 1 ? "visitor" : "visitors"}
                  </Typography>
                </Stack>
              </AnalyticsOverviewItem>
            </>
          )}
        </Grid>
      </Paper>
    </Stack>
  );
};
